/**
 * ProgressDashboard Component
 *
 * Comprehensive progress tracking dashboard showing:
 * - Progress matrix
 * - Personalized recommendations
 * - Performance trends
 * - Quick practice actions
 */

import React, { useState, useEffect } from 'react';
import {
  ChartColumn,
  CircleCheckBig,
  Dumbbell,
  FileText,
  Lightbulb,
  MoveRight,
  Target,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import ProgressMatrix from './ProgressMatrix';
import selectiveApi from '../../../services/selectiveApi';
import './ProgressDashboard.css';

const ProgressDashboard = ({ onStartPractice, onBack, yearLevel = 6 }) => {
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'matrix', 'trends'
  const [recommendations, setRecommendations] = useState([]);
  const [trends, setTrends] = useState(null);
  const [suggestedSession, setSuggestedSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgressData();
  }, []);

  const fetchProgressData = async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel
      const [recsData, trendsData, sessionData] = await Promise.all([
        selectiveApi.progress.getRecommendations({ limit: 5 }),
        selectiveApi.progress.getTrends(30),
        selectiveApi.progress.getSuggestedSession()
      ]);

      setRecommendations(recsData);
      setTrends(trendsData);
      setSuggestedSession(sessionData);
    } catch (err) {
      console.error('Failed to fetch progress data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (priority) => {
    if (priority >= 80) return { label: 'High', color: '#ef4444' };
    if (priority >= 60) return { label: 'Medium', color: '#f59e0b' };
    return { label: 'Low', color: '#3b82f6' };
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingUp size={24} />;
    if (trend === 'declining') return <TrendingDown size={24} />;
    return <MoveRight size={24} />;
  };

  const formatTrendLabel = (trend) => {
    if (trend === 'improving') return 'Improving';
    if (trend === 'declining') return 'Declining';
    if (trend === 'stable') return 'Stable';
    return '—';
  };

  const handleStartRecommended = (rec) => {
    if (onStartPractice) {
      onStartPractice({
        domain_code: rec.domain_code,
        tier: rec.recommended_tier
      });
    }
  };

  const handleStartSuggestedSession = () => {
    if (onStartPractice && suggestedSession) {
      // Map the backend session config to the launcher's initialConfig shape
      onStartPractice({
        section_code: suggestedSession.selected_sections?.[0],
        domain_code: suggestedSession.selected_domains?.[0],
        tier: suggestedSession.tier,
        question_count: suggestedSession.question_count,
      });
    }
  };

  return (
    <div className="progress-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        {onBack && (
          <button className="back-button" onClick={onBack}>
            ← Back to Dashboard
          </button>
        )}

        <div className="header-content">
          <h1>Progress Tracking</h1>
          <p className="progress-subtitle">
            Track your learning journey across all skills and tiers
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="dashboard-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <ChartColumn size={16} className="inline-icon" /> Overview
        </button>
        <button
          className={`tab-btn ${activeTab === 'matrix' ? 'active' : ''}`}
          onClick={() => setActiveTab('matrix')}
        >
          <Target size={16} className="inline-icon" /> Full Matrix
        </button>
        <button
          className={`tab-btn ${activeTab === 'trends' ? 'active' : ''}`}
          onClick={() => setActiveTab('trends')}
        >
          <TrendingUp size={16} className="inline-icon" /> Trends
        </button>
      </div>

      {/* Tab Content */}
      <div className="dashboard-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="overview-tab">
            {/* Suggested Session */}
            {suggestedSession && (
              <div className="suggested-session-card">
                <div className="card-header">
                  <h2><Target size={22} className="inline-icon" /> Suggested Practice Session</h2>
                  <p>Based on your current progress and learning patterns</p>
                </div>

                <div className="session-details">
                  <div className="session-info">
                    <div className="info-item">
                      <span className="info-label">Section:</span>
                      <span className="info-value">{suggestedSession.selected_sections?.[0] || '—'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Domain:</span>
                      <span className="info-value">{suggestedSession.selected_domains?.[0] || '—'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Tier:</span>
                      <span className="info-value">{suggestedSession.tier}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Questions:</span>
                      <span className="info-value">{suggestedSession.question_count}</span>
                    </div>
                  </div>

                  <div className="session-reason">
                    <strong>Why this session?</strong>
                    <p>{suggestedSession.reason}</p>
                  </div>
                </div>

                <button
                  className="start-session-btn"
                  onClick={handleStartSuggestedSession}
                >
                  Start This Session
                </button>
              </div>
            )}

            {/* Recommendations */}
            <div className="recommendations-section">
              <div className="section-header">
                <h2><Lightbulb size={22} className="inline-icon" /> Recommended Practice Areas</h2>
                <p>Focus on these areas to maximize your progress</p>
              </div>

              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading recommendations...</p>
                </div>
              ) : recommendations.length === 0 ? (
                <div className="empty-state">
                  <p>No recommendations yet. Complete some practice sessions to get started!</p>
                </div>
              ) : (
                <div className="recommendations-grid">
                  {recommendations.map((rec, index) => {
                    const badge = getPriorityBadge(rec.priority);

                    return (
                      <div key={index} className="recommendation-card">
                        <div className="rec-header">
                          <h3>{rec.domain_name}</h3>
                          <span
                            className="priority-badge"
                            style={{ backgroundColor: badge.color }}
                          >
                            {badge.label} Priority
                          </span>
                        </div>

                        <div className="rec-details">
                          <div className="rec-meta">
                            <span className="rec-tier">Tier: {rec.recommended_tier}</span>
                            <span className="rec-questions">
                              ~{rec.estimated_questions_to_master} questions to master
                            </span>
                          </div>

                          <p className="rec-reason">{rec.reason}</p>
                        </div>

                        <button
                          className="rec-practice-btn"
                          onClick={() => handleStartRecommended(rec)}
                        >
                          Start Practice
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Quick Stats */}
            {trends && (
              <div className="quick-stats-section">
                <h2><ChartColumn size={22} className="inline-icon" /> Recent Performance</h2>

                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon"><FileText size={24} /></div>
                    <div className="stat-content">
                      <div className="stat-value">{trends.total_sessions || 0}</div>
                      <div className="stat-label">Sessions (Last 30 Days)</div>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon"><CircleCheckBig size={24} /></div>
                    <div className="stat-content">
                      <div className="stat-value">{trends.average_score || 0}%</div>
                      <div className="stat-label">Average Score</div>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon">{getTrendIcon(trends.trend)}</div>
                    <div className="stat-content">
                      <div className="stat-value">{formatTrendLabel(trends.trend)}</div>
                      <div className="stat-label">Trend</div>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon"><TrendingUp size={24} /></div>
                    <div className="stat-content">
                      <div className="stat-value">
                        {trends.improvement_percentage > 0 ? '+' : ''}
                        {trends.improvement_percentage || 0}%
                      </div>
                      <div className="stat-label">Score Improvement</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Matrix Tab */}
        {activeTab === 'matrix' && (
          <div className="matrix-tab">
            <ProgressMatrix yearLevel={yearLevel} />
          </div>
        )}

        {/* Trends Tab */}
        {activeTab === 'trends' && (
          <div className="trends-tab">
            <div className="trends-header">
              <h2><TrendingUp size={22} className="inline-icon" /> Performance Trends</h2>
              <p>Analyze your progress over the last 30 days</p>
            </div>

            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading trends...</p>
              </div>
            ) : !trends || trends.trend === 'no_data' || trends.trend === 'error' ? (
              <div className="empty-state">
                <p>Not enough data yet. Complete more practice sessions!</p>
              </div>
            ) : (
              <div className="trends-content">
                {/* Overall Trend */}
                <div className="trend-card">
                  <h3>Overall Performance</h3>
                  {trends.message && (
                    <p className="trend-message">{trends.message}</p>
                  )}
                  <div className="trend-summary">
                    <div className="trend-metric">
                      <span className="metric-label">Sessions Completed</span>
                      <span className="metric-value">{trends.total_sessions || 0}</span>
                    </div>
                    <div className="trend-metric">
                      <span className="metric-label">Average Score</span>
                      <span className="metric-value">{trends.average_score || 0}%</span>
                    </div>
                    <div className="trend-metric">
                      <span className="metric-label">Score Change</span>
                      <span className={`metric-value ${trends.improvement_percentage >= 0 ? 'positive' : 'negative'}`}>
                        {trends.improvement_percentage > 0 ? '+' : ''}
                        {trends.improvement_percentage || 0}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Section Breakdown */}
                {trends.section_performance && (
                  <div className="trend-card">
                    <h3>Performance by Section</h3>
                    <div className="section-performance-list">
                      {Object.entries(trends.section_performance).map(([section, data]) => (
                        <div key={section} className="section-performance-item">
                          <div className="section-performance-header">
                            <span className="section-name">{section}</span>
                            <span className="section-score">{data.average_score}%</span>
                          </div>
                          <div className="section-performance-bar">
                            <div
                              className="performance-bar-fill"
                              style={{
                                width: `${data.average_score}%`,
                                backgroundColor: data.average_score >= 75 ? '#10b981' : data.average_score >= 60 ? '#f59e0b' : '#3b82f6'
                              }}
                            />
                          </div>
                          <div className="section-performance-meta">
                            <span>{data.sessions_count} sessions</span>
                            <span>
                              {data.improvement > 0 ? '↗' : data.improvement < 0 ? '↘' : '→'}
                              {data.improvement > 0 ? '+' : ''}{data.improvement}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Strengths & Weaknesses */}
                <div className="strengths-weaknesses">
                  {trends.top_strengths && trends.top_strengths.length > 0 && (
                    <div className="trend-card strengths-card">
                      <h3><Dumbbell size={18} className="inline-icon" /> Top Strengths</h3>
                      <ul className="strength-list">
                        {trends.top_strengths.map((strength, index) => (
                          <li key={index}>
                            <span className="strength-domain">{strength.domain_name}</span>
                            <span className="strength-score">{strength.score}%</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {trends.areas_to_improve && trends.areas_to_improve.length > 0 && (
                    <div className="trend-card weakness-card">
                      <h3><Target size={18} className="inline-icon" /> Areas to Improve</h3>
                      <ul className="weakness-list">
                        {trends.areas_to_improve.map((area, index) => (
                          <li key={index}>
                            <span className="weakness-domain">{area.domain_name}</span>
                            <span className="weakness-score">{area.score}%</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressDashboard;
